import motion from "../../motion-tokens.json" with { type: "json" };

/**
 * The Insights page's count-up and stagger (D58, D74 of documentation/pr-plans/insights-s0-plan-and-seats.md),
 * on the motion tokens (resources/js/motion-tokens.json). Each leaves the element as the server drew it
 * under reduced motion, and ends on it otherwise. The list directive, x-list-motion, is in list-motion.js,
 * apart from these so this module imports no package and runs under Node as it does in the browser
 * (InsightsMotionHelpersTest).
 */

/** Checked on each use, so a reader who changes the setting gets it on the next screen. */
export function motionAllowed() {
    return typeof window.matchMedia !== "function" || !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Each screen's first showing, by its data-insights-screen (or the whole page, for an element outside
// any): open while the screen's helpers start, which is one pass of Alpine's init, and shown a frame
// later, so a figure or list drawn again on that screen, after a window change or a return to it,
// never moves again on this page load (D74: once per screen per page load).
const screens = new Map();

function isFirstShowing(el) {
    const key = el.closest?.("[data-insights-screen]")?.getAttribute("data-insights-screen") ?? "";

    if (screens.get(key) === "shown") {
        return false;
    }

    if (!screens.has(key)) {
        screens.set(key, "open");
        requestAnimationFrame(() => screens.set(key, "shown"));
    }

    return true;
}

/**
 * A figure as the server prints it ("+1,240", "−18.2%", "0.90"), split into the number and the text
 * around it, or null for text that holds no number or more than one ("3 → 7"), which is left as it is.
 *
 * @returns {{prefix: string, suffix: string, value: number, decimals: number, grouped: boolean}|null}
 */
export function parseFigure(text) {
    const match = /^(\D*?)(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?(\D*)$/.exec(text);

    if (match === null) {
        return null;
    }

    const [, prefix, whole, fraction = "", suffix] = match;

    return {
        prefix,
        suffix,
        value: Number(whole.replaceAll(",", "") + (fraction === "" ? "" : "." + fraction)),
        decimals: fraction.length,
        grouped: whole.includes(","),
    };
}

/** A number on the way to `figure`, printed as the figure is: its decimals, its commas and its text. */
export function formatFigure(figure, value) {
    const [whole, fraction] = value.toFixed(figure.decimals).split(".");
    const grouped = figure.grouped ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : whole;

    return figure.prefix + grouped + (fraction === undefined ? "" : "." + fraction) + figure.suffix;
}

export const easeOutCubic = (t) => 1 - (1 - t) ** 3;

/**
 * x-count-up on an element whose text is a figure: the first time its screen shows, the figure counts
 * up from 0 over the `view` token on an ease-out cubic, and its last frame puts back the text exactly as
 * it was drawn. Whatever else writes the element meanwhile (a Livewire morph, an x-text) wins, and the
 * count stops there.
 */
export function CountUpDirective(el) {
    if (!motionAllowed() || !isFirstShowing(el)) {
        return;
    }

    // After the init pass, so an x-text on the same element has written the figure first.
    queueMicrotask(() => {
        const text = el.textContent;
        const figure = parseFigure(text);

        if (figure === null || figure.value === 0) {
            return;
        }

        let written = formatFigure(figure, 0);
        let start = null;
        el.textContent = written;

        const frame = (now) => {
            if (el.textContent !== written) {
                return;
            }

            start ??= now;
            const progress = Math.min(1, (now - start) / motion.duration.view);
            written = progress < 1 ? formatFigure(figure, figure.value * easeOutCubic(progress)) : text;
            el.textContent = written;

            if (progress < 1) {
                requestAnimationFrame(frame);
            }
        };

        requestAnimationFrame(frame);
    });
}

/**
 * x-stagger on a list's parent: the first time its screen shows, its children fade up 6px one after
 * another, a `staggerStep` apart; past the `staggerMax`th they all start with the last, so the whole
 * run ends within the `view` token.
 */
export function StaggerDirective(el) {
    if (!motionAllowed() || !isFirstShowing(el)) {
        return;
    }

    // After the init pass, so the rows an x-for draws are there to move.
    queueMicrotask(() => {
        [...el.children]
            .filter((child) => child.tagName !== "TEMPLATE" && typeof child.animate === "function")
            .forEach((child, index) => {
                child.animate(
                    [
                        { opacity: 0, transform: "translateY(6px)" },
                        { opacity: 1, transform: "none" },
                    ],
                    {
                        duration: motion.duration.show,
                        easing: motion.ease,
                        delay: Math.min(index, motion.staggerMax - 1) * motion.staggerStep,
                        fill: "backwards",
                    },
                );
            });
    });
}
